<template>
  <div class="min-h-screen bg-gray-100">
    <TopBar :role="this.role" :user-data="this.userData.data" v-if="!isTeacherViewingStudent && userData && role" />

    <main class="max-w-[1300px] m-auto py-4 px-2 sm:px-4">
      <div class="bg-white rounded-2xl shadow-sm">
        <div class="flex flex-col gap-3 md:flex-row md:justify-between md:items-center p-3 sm:p-4 md:p-5">
          <div class="space-y-1 md:space-y-4 flex items-center gap-3">
            <router-link
              v-if="isTeacherViewingStudent"
              to="/teacher/groups"
              class="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-800 shrink-0"
            >
              <Icon icon="ph:arrow-left" height="20" />
              Volver
            </router-link>
            <div>
              <h2 class="text-2xl font-bold">{{ isTeacherViewingStudent ? "Reporte de desempeño" : "Estadísticas" }}</h2>
              <p class="text-sm text-gray-500">
                {{ isTeacherViewingStudent && viewingStudentName ? `Estudiante: ${viewingStudentName}` : "Este es el resumen de tu desempeño en los cursos" }}
              </p>
            </div>
          </div>
        </div>

        <div class="p-3 sm:p-4 md:p-5 pt-0">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div class="lg:col-span-5">
              <div class="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-5 transition-all duration-300 hover:shadow-md">
                <div class="flex flex-col gap-1 mb-4">
                  <h3 class="text-xl font-bold text-gray-800">{{ isTeacherViewingStudent ? 'Precisión General' : 'Tu Progreso en Precisión General' }}</h3>
                  <p class="text-sm text-gray-500">Evolución de la precisión según el rango seleccionado</p>
                </div>

                <div class="flex flex-wrap items-center justify-center gap-2 mb-4">
                  <button @click="setTimeRange('weeks')" :class="[
                    'py-2 px-4 rounded-full text-sm font-medium transition-colors duration-200',
                    timeRange === 'weeks' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  ]">
                    Últimas 4 Semanas
                  </button>

                  <button @click="setTimeRange('months')" :class="[
                    'py-2 px-4 rounded-full text-sm font-medium transition-colors duration-200',
                    timeRange === 'months' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  ]">
                    Últimos 3 Meses
                  </button>

                  <button @click="setTimeRange('all')" :class="[
                    'py-2 px-4 rounded-full text-sm font-medium transition-colors duration-200',
                    timeRange === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  ]">
                    Todo el Tiempo
                  </button>
                </div>

                <OverallAccuracyGraph :received-time-range="timeRange" :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
              </div>
            </div>
            <div class="lg:col-span-7 flex flex-col gap-4">
              <div class="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <h3 class="text-base font-semibold text-neutral-800">Reporte Personal</h3>
                  <button
                    v-if="showReportButton && !isTeacherViewingStudent"
                    type="button"
                    @click="generatePersonalReport"
                    :disabled="reportLoading"
                    class="mob-btn mob-cta-gold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ reportLoading ? "Generando..." : "Generar Reporte Personal" }}
                  </button>
                </div>

                <div v-if="reportError" class="mt-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                  {{ reportError }}
                </div>

                <div v-if="reportLoading" class="mt-3 flex items-center gap-3 text-sm text-neutral-500">
                  <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-transparent"></span>
                  Generando reporte...
                </div>

                <div class="mt-4 space-y-4">
                  <div>
                    <h4 class="text-sm font-semibold text-neutral-700">Reporte</h4>
                    <p class="mt-2 text-sm text-neutral-500 whitespace-pre-wrap">
                      {{ displayReportSummary }}
                    </p>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-neutral-700">Recomendaciones</h4>
                    <p class="mt-2 text-sm text-neutral-500 whitespace-pre-wrap">
                      {{ displayReportRecommendations }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <OverallAccuracyKPI :received-time-range="timeRange" :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
                <TotalQuestions :received-time-range="timeRange" :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
                <CurrentStreak :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
                <StudyTime :received-time-range="timeRange" :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
              </div>
            </div>
          </div>
        </div>

        <div class="p-3 sm:p-4 md:p-5 pt-0">
          <div class="space-y-4">
            <BestAndWorstSubcategories :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
            <CategoryDetailedPerformance :is-premium="effectiveIsPremiumForReport" :for-student-id="resolvedStudentIdForStats" />
          </div>
        </div>

        <MobPaymentModal v-if="!isTeacherViewingStudent" ref="mobPaymentModal" :prevent-close="true" :hide-header-close="true" :show-later-button="true" />
      </div>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import { Icon } from "@iconify/vue2";
import TopBar from "@/views/V2/dashboard/components/TopBar.vue";
import { getStudentReport } from "@/services/teacher-groups.service";

import OverallAccuracyKPI from "./components/OverallAccuracyKPI.vue";
import TotalQuestions from "./components/TotalQuestions.vue";
import CurrentStreak from "./components/CurrentStreak.vue";
import StudyTime from "./components/StudyTime.vue";
import BestAndWorstSubcategories from "./components/BestAndWorstSubcategories.vue";
import OverallAccuracyGraph from "./components/OverallAccuracyGraph.vue";
import CategoryDetailedPerformance from "./components/CategoryDetailedPerformance.vue";
import MobPaymentModal from "../payment-premium/PaymentModal.vue";

export default {
  name: "Statistics",

  components: {
    Icon,
    TopBar,
    OverallAccuracyKPI,
    TotalQuestions,
    CurrentStreak,
    StudyTime,
    BestAndWorstSubcategories,
    OverallAccuracyGraph,
    CategoryDetailedPerformance,
    MobPaymentModal
  },

  data() {
    return {
      role: "",
      userData: [],
      timeRange: "weeks", // Estado para el rango de tiempo seleccionado
      reportSummary: "",
      reportRecommendations: "",
      reportLoading: false,
      reportError: null,
      showReportButton: false,
      viewingStudentName: "",
    };
  },

  computed: {
    isTeacherViewingStudent() {
      if (this.viewingStudentId) return true;
      const r = this.$route;
      return r && (r.name === "TeacherStudentReport" || (r.path && /\/teacher\/students\/\d+\/report$/.test(r.path)));
    },
    effectiveIsPremiumForReport() {
      return this.isPremiumUser === true || this.isTeacherViewingStudent;
    },
    resolvedStudentIdForStats() {
      if (!this.isTeacherViewingStudent) return null;
      const id = this.viewingStudentId || (this.$route.params && this.$route.params.studentId);
      return id != null ? Number(id) : null;
    },
    isPremiumUser() {
      return this.$store.state.auth.isPremium;
    },
    displayReportSummary() {
      const raw = this.reportSummary || "";
      const cleaned = this.stripReportMarkers(raw);
      return cleaned || "Aquí irá el reporte personal con el detalle del desempeño del estudiante.";
    },
    displayReportRecommendations() {
      const raw = this.reportRecommendations || "";
      const cleaned = this.stripReportMarkers(raw);
      return cleaned || "Aquí irán las recomendaciones personalizadas para el estudiante.";
    },
  },

  async created() {
    this.role = window.localStorage.getItem("role");
    this.userData = JSON.parse(window.localStorage.getItem("auth"));

    if (this.isTeacherViewingStudent) {
      await this.loadStudentReportForTeacher();
      return;
    }

    // Si isPremium es null, obtener el estado premium del servidor
    if (this.isPremiumUser === null) {
      await this.$store.dispatch("auth/fetchPremiumStatus");
    }

    this.loadCachedReport();
    this.maybeAutoGeneratePersonalReport();
  },

  mounted() {
    if (this.isTeacherViewingStudent) return;
    // Verificar después de que el estado premium se haya cargado
    this.checkPremiumStatus();
  },

  watch: {
    isPremiumUser(newValue) {
      if (this.isTeacherViewingStudent) return;
      // Cuando el estado premium cambia, verificar si mostrar el modal
      if (newValue !== null) {
        this.checkPremiumStatus();
      }
      if (newValue) {
        this.maybeAutoGeneratePersonalReport();
      }
    }
  },

  methods: {
    stripReportMarkers(text) {
      if (!text || typeof text !== "string") return "";
      return text
        .split(/\r?\n/)
        .filter((line) => !/===[A-Z_]+===\s*$/.test(line.trim()))
        .join("\n")
        .trim();
    },
    getReportStorageKey() {
      const userId = this.userData?.data?.id || this.userData?.id || "anonymous";
      return `personal-report:last-run:${userId}`;
    },
    getReportCacheKey() {
      const userId = this.userData?.data?.id || this.userData?.id || "anonymous";
      return `personal-report:cache:${userId}`;
    },
    loadCachedReport() {
      const cacheKey = this.getReportCacheKey();
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return;
      try {
        const parsed = JSON.parse(cached);
        this.reportSummary = parsed.summary || "";
        this.reportRecommendations = parsed.recommendations || "";
      } catch (error) {
        // Ignore corrupted cache
      }
    },
    shouldAutoGenerateReport() {
      const key = this.getReportStorageKey();
      const lastRun = localStorage.getItem(key);
      if (!lastRun) return true;

      const now = new Date();
      const lastRunDate = new Date(lastRun);
      if (Number.isNaN(lastRunDate.getTime())) return true;

      const diffMs = now.getTime() - lastRunDate.getTime();
      const oneDayMs = 24 * 60 * 60 * 1000;
      return diffMs >= oneDayMs;
    },
    markReportRun() {
      const key = this.getReportStorageKey();
      localStorage.setItem(key, new Date().toISOString());
    },
    cacheReport(summary, recommendations) {
      const cacheKey = this.getReportCacheKey();
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          summary,
          recommendations,
          cachedAt: new Date().toISOString(),
        })
      );
    },
    maybeAutoGeneratePersonalReport() {
      if (!this.isPremiumUser || this.reportLoading) return;
      if (!this.shouldAutoGenerateReport()) return;
      this.generatePersonalReport();
    },
    setTimeRange(selectedRange) {
      this.timeRange = selectedRange;
    },
    checkPremiumStatus() {
      if (this.isTeacherViewingStudent) return;
      // Solo mostrar el modal si isPremium es explícitamente false (no null)
      if (this.isPremiumUser === false && this.$refs["mobPaymentModal"]) {
        this.$refs["mobPaymentModal"].showModalPayment();
      }
    },
    async loadStudentReportForTeacher() {
      const studentId = this.viewingStudentId || (this.$route.params && this.$route.params.studentId);
      if (!studentId) return;
      this.viewingStudentId = studentId;
      this.reportLoading = true;
      this.reportError = null;
      try {
        const data = await getStudentReport(Number(studentId));
        this.reportSummary = data.summary || "";
        this.reportRecommendations = data.recommendations || "";
        this.viewingStudentName = data.studentName || "";
      } catch (err) {
        this.reportError = err.response?.data || "No se pudo cargar el reporte del estudiante.";
      } finally {
        this.reportLoading = false;
      }
    },
    async generatePersonalReport() {
      this.reportLoading = true;
      this.reportError = null;
      this.reportSummary = "";
      this.reportRecommendations = "";

      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/api/attempts/summary/chatgpt/personal-report`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          this.reportSummary = response.data.summary || "";
          this.reportRecommendations = response.data.recommendations || "";
          this.markReportRun();
          this.cacheReport(this.reportSummary, this.reportRecommendations);
        }
      } catch (error) {
        let errorMessage = error.message || "No se pudo generar el reporte. Intenta de nuevo.";

        if (error.response?.data) {
          if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        }

        this.reportError = errorMessage;
      } finally {
        this.reportLoading = false;
      }
    },
  },
};
</script>
